/**
 * Invite a client teammate.
 *
 * Opened from the consultant's client page header (ClientDetailView) and from
 * the client Engagement Lead's avatar menu (UserAvatarMenu). The server creates
 * the account and emails a set-your-password link on the firm's subdomain;
 * people who have not signed in yet are listed with "Resend invitation".
 */

import { useState, useEffect, useCallback, type FormEvent } from 'react';
import { toast } from 'sonner';
import { Loader2, Mail, UserPlus } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  CLIENT_INVITE_ROLES,
  inviteClientUser,
  listPendingInvites,
  resendClientInvite,
  roleLabel,
  type ClientInviteRole,
  type PendingInvite,
} from '../utils/clientInvitesApi';

interface InviteClientUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  companyName?: string;
  accessToken: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function InviteClientUserDialog({ open, onOpenChange, companyId, companyName, accessToken }: InviteClientUserDialogProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<ClientInviteRole>('user');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [pending, setPending] = useState<PendingInvite[]>([]);
  const [loadingPending, setLoadingPending] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);

  const loadPending = useCallback(async () => {
    setLoadingPending(true);
    try {
      setPending(await listPendingInvites(companyId, accessToken));
    } catch (err) {
      console.error('[INVITE] could not load pending invitations:', err);
    } finally {
      setLoadingPending(false);
    }
  }, [companyId, accessToken]);

  useEffect(() => {
    if (open) {
      setError('');
      void loadPending();
    }
  }, [open, loadPending]);

  const handleInvite = async (e: FormEvent) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    if (!EMAIL_RE.test(trimmedEmail)) {
      setError('Enter a valid email address');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const result = await inviteClientUser(companyId, { name: name.trim(), email: trimmedEmail, role }, accessToken);
      if (result.emailSent) {
        toast.success(result.resent ? `Invitation re-sent to ${trimmedEmail}` : `Invitation sent to ${trimmedEmail}`);
      } else {
        toast.warning(`${trimmedEmail} was added, but the email did not send. Use "Resend invitation" to try again.`);
      }
      setName('');
      setEmail('');
      setRole('user');
      await loadPending();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the invitation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async (person: PendingInvite) => {
    setResendingId(person.id);
    try {
      const result = await resendClientInvite(companyId, person.id, accessToken);
      if (result.emailSent) toast.success(`Invitation re-sent to ${person.email}`);
      else toast.error(`The email to ${person.email} did not send${result.emailError ? `: ${result.emailError}` : ''}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not resend the invitation');
    } finally {
      setResendingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="size-5" />
            Invite a teammate
          </DialogTitle>
          <DialogDescription>
            {companyName ? `They join ${companyName} and get an email to set their password.` : 'They get an email to set their password.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleInvite} className="space-y-4" noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="invite-name">Name</Label>
            <Input id="invite-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jordan Lee" autoComplete="off" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="invite-email">Email</Label>
            <Input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jordan@company.com"
              autoComplete="off"
              required
            />
          </div>
          <fieldset className="space-y-2">
            <legend className="mb-1.5">Role</legend>
            {CLIENT_INVITE_ROLES.map((r) => (
              <label
                key={r.value}
                className={`flex items-start gap-3 rounded-md border p-3 cursor-pointer ${role === r.value ? 'border-primary bg-primary/5' : 'border-border'}`}
              >
                <input
                  type="radio"
                  name="invite-role"
                  value={r.value}
                  checked={role === r.value}
                  onChange={() => setRole(r.value)}
                  className="mt-1"
                />
                <span>
                  <span className="block">{r.label}</span>
                  <span className="block text-muted-foreground">{r.description}</span>
                </span>
              </label>
            ))}
          </fieldset>

          {error && (
            <p role="alert" className="text-destructive">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
            <Button type="submit" disabled={submitting || !email.trim()}>
              {submitting ? <Loader2 className="size-4 mr-2 animate-spin" /> : <Mail className="size-4 mr-2" />}
              Send invitation
            </Button>
          </DialogFooter>
        </form>

        {(loadingPending || pending.length > 0) && (
          <div className="border-t pt-4 space-y-2">
            <p>Waiting to sign in</p>
            {loadingPending && pending.length === 0 ? (
              <Loader2 className="size-4 animate-spin text-muted-foreground" />
            ) : (
              <ul className="space-y-2" data-testid="pending-invites">
                {pending.map((p) => (
                  <li key={p.id} className="flex items-center justify-between gap-3">
                    <span className="min-w-0">
                      <span className="block truncate">{p.name || p.email}</span>
                      <span className="block truncate text-muted-foreground">
                        {p.name ? `${p.email} · ` : ''}{roleLabel(p.role)}
                      </span>
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleResend(p)}
                      disabled={resendingId === p.id}
                    >
                      {resendingId === p.id ? <Loader2 className="size-4 animate-spin" /> : 'Resend invitation'}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
